import { revalidateLogic } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { trim } from 'lodash';
import { toast } from 'react-toastify';
import { usaStates } from 'typed-usa-states';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useAppForm } from '@/app/lib/tanstack-form';
import { CreateApplicationRequest, InitialIntakeResponseNextStep } from '@/app/network/models/v3/applications';
import { setApplicantEmail } from '@/applications/applicant-email';
import { useCreateApplication } from '@/applications/queries';
import { Alert } from '@/common/components/alert';
import { ButtonGroup } from '@/common/components/button';
import { Field, FieldGroup, FieldInfo, FieldRow, Fieldset, Label, Legend } from '@/common/components/fieldset';
import { FormInput } from '@/common/components/form-input';
import { FormSelect } from '@/common/components/form-select';
import { Heading } from '@/common/components/heading';
import { emptyToUndefined, isNullOrWhitespace } from '@/common/string';
import { useAuth } from '@/shared/auth/use-auth';
import { Toast } from '@/shared/components/toast';
import { phoneNumberMask } from '@/shared/masks';

export const Route = createFileRoute('/apply/residential')({
  component: ResidentialRoute
});

function ResidentialRoute() {
  const { user } = useAuth();
  const { data: userData } = user;
  const createApplication = useCreateApplication();

  const navigate = useNavigate();

  const form = useAppForm({
    defaultValues: {
      firstName: userData?.first_name ?? '',
      middleName: '',
      lastName: userData?.last_name ?? '',
      suffix: '',
      phone: '',
      email: userData?.email ?? '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: ''
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change'
    }),
    validators: {
      onDynamic: residentialServiceFormSchema
    },
    onSubmit: async ({ value }) => {
      await createApplication.mutateAsync(
        mapResidentialFormToCreateRequest(value),
        {
          onSuccess: (data) => {
            toast.success(Toast, { data: {
              title: 'Application Submitted',
              content: 'Your application has been submitted successfully'
            } });

            setApplicantEmail(value.email);

            if (data.nextStep === InitialIntakeResponseNextStep.Continue) {
              navigate({
                to: '/applications/$application-id/service-location',
                params: { 'application-id': data.applicationId.toString() }
              }); // TODO: should be index to determine how an application continues
            } else {
              navigate({ to: '/apply/verify-email' });
            }
          },
          onError: error => handleUnexpectedError(error)
        }
      );
    }
  });

  return (
    <div className="p-8">
      <Heading>Apply for Residential Service</Heading>

      <Alert type="info" className="my-4">
        This application process includes a credit check. If you have frozen your credit with the credit reporting agencies, please temporarily lift the freeze during this application process.
      </Alert>

      <Alert type="warning" className="my-4">
        Please enter your <strong>full legal name</strong> and all information accurately. Failure to do so will require verification of identity at a Member Services location.
      </Alert>

      <form onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
      >
        <Fieldset>
          <Legend>Full Legal Name</Legend>

          <FieldGroup>
            <FieldRow cols={4}>
              <form.AppField
                name="firstName"
                children={field => (
                  <Field>
                    <Label>First Name</Label>
                    <FormInput type="text" placeholder="John" />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="middleName"
                children={field => (
                  <Field>
                    <Label>Middle Name (Optional)</Label>
                    <FormInput type="text" />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="lastName"
                children={field => (
                  <Field>
                    <Label>Last Name</Label>
                    <FormInput placeholder="Doe" />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="suffix"
                children={field => (
                  <Field>
                    <Label>Suffix (Optional)</Label>
                    <FormInput placeholder="Jr." />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />
            </FieldRow>
          </FieldGroup>
        </Fieldset>

        <Fieldset className="mt-4">
          <Legend>Contact Information</Legend>

          <FieldGroup>
            <FieldRow cols={2}>
              <form.AppField
                name="phone"
                children={field => (
                  <Field>
                    <Label>Phone</Label>
                    <FormInput
                      type="text"
                      mask={phoneNumberMask}
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="(555) 555-5555"
                    />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="email"
                children={field => (
                  <Field>
                    <Label>Email Address</Label>
                    <FormInput type="email" disabled={!isNullOrWhitespace(userData?.email)}/>
                    <FieldInfo field={field} />
                  </Field>
                )}
              />
            </FieldRow>
          </FieldGroup>
        </Fieldset>

        <Fieldset className="mt-4">
          <Legend>Mailing Address</Legend>

          <FieldGroup>
            <FieldRow cols={2}>
              <form.AppField
                name="addressLine1"
                children={field => (
                  <Field>
                    <Label>Address Line 1</Label>
                    <FormInput placeholder="1607 W Jefferson St."/>
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="addressLine2"
                children={field => (
                  <Field>
                    <Label>Address Line 2 (Optional)</Label>
                    <FormInput />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />
            </FieldRow>
            <FieldRow cols={6}>
              <form.AppField
                name="city"
                children={field => (
                  <Field className="lg:col-span-3">
                    <Label>City</Label>
                    <FormInput placeholder="Quincy" />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="state"
                children={field => (
                  <Field className="lg:col-span-2">
                    <Label>State</Label>
                    <FormSelect>
                      <option></option>
                      {usaStates.map(({ abbreviation, name }) => (
                        <option key={abbreviation} value={abbreviation}>{name}</option>
                      ))}
                    </FormSelect>
                    <FieldInfo field={field} />
                  </Field>
                )}
              />

              <form.AppField
                name="zipCode"
                children={field => (
                  <Field>
                    <Label>ZIP Code</Label>
                    <FormInput placeholder="32353" />
                    <FieldInfo field={field} />
                  </Field>
                )}
              />
            </FieldRow>
          </FieldGroup>
        </Fieldset>

        <form.AppForm>
          <ButtonGroup>
            <form.FormButton type="submit">Next</form.FormButton>
          </ButtonGroup>
        </form.AppForm>
      </form>
    </div>
  );
}

function mapResidentialFormToCreateRequest(formData: z.infer<typeof residentialServiceFormSchema>) {
  return {
    firstName: trim(formData.firstName),
    middleName: emptyToUndefined(formData.middleName),
    lastName: trim(formData.lastName),
    suffix: emptyToUndefined(formData.suffix),
    phoneNumber: trim(formData.phone),
    email: trim(formData.email),
    mailingAddress: {
      line1: trim(formData.addressLine1),
      line2: emptyToUndefined(formData.addressLine2),
      city: trim(formData.city),
      state: trim(formData.state),
      zipCode: trim(formData.zipCode)
    }
  } satisfies CreateApplicationRequest;
}

const residentialServiceFormSchema = z.object({
  firstName: z.string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(100, 'First name cannot exceed 100 characters'),
  middleName: z.string()
    .max(50, 'Middle name cannot exceed 50 characters'),
  lastName: z.string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(100, 'Last name cannot exceed 100 characters'),
  suffix: z.string()
    .max(199, 'Suffix cannot exceed 100 characters'),
  phone: z.string()
    .min(1, 'Phone number is required')
    .min(10, 'Phone number must be at least 10 characters')
    .max(15, 'Phone number cannot exceed 15 characters'),
  email: z.email('Enter a valid email')
    .min(1, 'Email is required'),
  addressLine1: z.string()
    .min(1, 'Address line 1 is required')
    .min(5, 'Address line 1 must be at least 5 characters')
    .max(100, 'Address line 1 cannot exceed 100 characters'),
  addressLine2: z.string()
    .max(100, 'Address line 2 cannot exceed 100 characters'),
  city: z.string()
    .min(1, 'City is required')
    .min(2, 'City must be at least 2 characters')
    .max(100, 'City cannot exceed 100 characters'),
  state: z.string()
    .min(2, 'State is required')
    .max(2, 'State cannot exceed 2 characters'),
  zipCode: z.string()
    .min(5, 'Zip code must be at least 5 characters')
    .max(10, 'Zip code cannot exceed 10 characters')
});
